import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, TrendingUp, MessageCircle, Globe, Wallet, CreditCard, AlertTriangle, Coins, MessageSquare, RefreshCw, Scale, BookOpen, ExternalLink, Play, X, Activity, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api, { systemService } from '../services/api';
import { useNotificationStore } from '../stores/useNotificationStore';
import TelegramIntelPanel from '../components/TelegramIntelPanel';
import ChannelValidationModal, { type ChannelValidationResult } from '../components/ChannelValidationModal';
import { DownwardSelect } from '../components/DownwardSelect';

interface DetailedStats {
    binance_p2p?: {
        total_ads: number;
        unique_traders: number;
        sell_volume: number;
        buy_volume: number;
        last_run: string | null;
        assets: Array<{ name: string; count: number; volume: number }>;
    };
    telegram?: {
        total_messages: number;
        channels_scraped: number;
        unique_upi_ids: number;
        unique_wallets: number;
        last_run: string | null;
        top_channels: Array<{ name: string; count: number }>;
    };
    entities?: {
        total: number;
        critical_risk: number;
        high_risk: number;
        india_linked: number;
        last_run: string | null;
        top_sources: Array<{ name: string; count: number }>;
    };
    wallet_enrichment?: {
        total_enriched: number;
        high_value_wallets: number;
        last_run: string | null;
        balance_distribution: Array<{ range: string; count: number }>;
        top_wallets: Array<{ address: string; chain: string; balance: number }>;
    };
    bitcointalk?: {
        total_reports: number;
        unique_addresses: number;
        chains: { [key: string]: number };
        last_scraped: string | null;
    };
    chainabuse?: {
        total: number;
        last_run: string | null;
        chains: { [key: string]: number };
    };
    reddit?: {
        total_reports: number;
        unique_addresses: number;
        last_scraped: string | null;
    };
    ecourts?: {
        total_articles: number;
        unique_addresses: number;
        last_scraped: string | null;
    };
}

interface RecentEntity {
    address: string;
    chain: string;
    source: string;
    entity_name?: string;
    risk_score?: number;
    created_at: string;
}

interface TelegramPreview {
    total_scanned?: number;
    relevant_messages?: number;
    unique_upi_ids?: number;
    unique_wallets?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    samples?: any[];
}

interface TelegramJobSummary {
    saved_messages?: number;
    total_scanned?: number;
    unique_upi_ids?: number;
    unique_wallets?: number;
}

interface TelegramJobStatus {
    status: string;
    progress?: number;
    summary?: TelegramJobSummary;
    error?: string;
}

interface TelegramMessage {
    id: number;
    channel: string;
    content: string;
    upi_ids: string[];
    crypto_addresses: string[];
    extracted_exchanges?: string[];
    sender: string;
    scraped_at: string | null;
}

interface ScraperListItem {
    name: string;
    description: string;
    enabled: boolean;
}

interface ScraperStatusItem {
    scraper_name?: string;
    name?: string;
    last_run?: string | null;
    status?: string;
}

interface PulseItem {
    type: string;
    source: string;
    timestamp: string;
    content: string;
    identifiers: string[];
    crypto: string[];
}

type SourceHealthKind = 'live' | 'stale' | 'error' | 'never' | 'empty';

type UpstreamProviderStatus = 'ready' | 'degraded' | 'circuit_open' | 'unconfigured';

interface UpstreamProviderRow {
    name: string;
    display_name: string;
    category: string;
    cascade_role: string;
    capabilities: string[];
    chains: string[];
    configured: boolean;
    status: UpstreamProviderStatus;
    circuit_open: boolean;
    trip_reason?: string | null;
    recent_failures: number;
}

interface UpstreamProvidersPayload {
    providers?: UpstreamProviderRow[];
    summary?: {
        total?: number;
        ready?: number;
        degraded?: number;
        circuit_open?: number;
        unconfigured?: number;
    };
}

function classifySourceHealth(lastRun: string | null | undefined, rowCount?: number, status?: string): SourceHealthKind {
    if (status === 'error') return 'error';
    if (!lastRun) {
        if (rowCount === 0 || rowCount === undefined) return 'never';
        return 'stale';
    }
    const ageH = (Date.now() - new Date(lastRun).getTime()) / (1000 * 3600);
    if (Number.isNaN(ageH)) return 'never';
    if (rowCount === 0) return 'empty';
    if (ageH < 24) return 'live';
    return 'stale';
}

function SourceHealthBadge({ lastRun, rowCount, status }: { lastRun?: string | null; rowCount?: number; status?: string }) {
    const kind = classifySourceHealth(lastRun, rowCount, status);
    const label =
        kind === 'live' ? 'Live'
            : kind === 'stale' ? 'Stale'
                : kind === 'error' ? 'Error'
                    : kind === 'empty' ? '0 rows'
                        : 'Never run';
    return (
        <span
            className="ml-auto shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border border-border bg-muted text-muted-foreground"
            title={lastRun ? `Last run: ${new Date(lastRun).toLocaleString()}` : 'No successful run recorded'}
        >
            {label}
        </span>
    );
}

function UpstreamProviderBadge({ status, tripReason }: { status: UpstreamProviderStatus; tripReason?: string | null }) {
    const label =
        status === 'ready' ? 'Ready'
            : status === 'degraded' ? 'Degraded'
                : status === 'circuit_open' ? 'Paused'
                    : 'Not configured';
    const title =
        status === 'circuit_open' && tripReason
            ? `Circuit open: ${tripReason}`
            : status === 'degraded'
                ? 'Recent failures — may fall back to alternate providers'
                : undefined;
    return (
        <span
            className="shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md border border-border bg-muted text-muted-foreground"
            title={title}
        >
            {label}
        </span>
    );
}

const HARDCODED_SCRAPER_NAME_FRAGMENTS = [
    'binance', 'telegram', 'entity', 'wallet', 'bitcointalk', 'chainabuse', 'reddit', 'ecourt',
];

const PULSE_TYPE_CLASS: Record<string, string> = {
    p2p_anomaly: 'bg-destructive/10 text-destructive',
    entity: 'bg-muted text-foreground',
    telegram: 'bg-primary/10 text-primary',
};

export const DataSources = () => {
    const navigate = useNavigate();
    const [detailedStats, setDetailedStats] = useState<DetailedStats>({});
    const [statsError, setStatsError] = useState(false);
    const [recentEntities, setRecentEntities] = useState<RecentEntity[]>([]);
    const [loading, setLoading] = useState(true);
    const [telegramModalOpen, setTelegramModalOpen] = useState(false);
    const [telegramChannels, setTelegramChannels] = useState<Array<{ name: string; enabled: boolean; source?: string }>>([]);
    const [telegramSelected, setTelegramSelected] = useState<string[]>([]);
    const [telegramNewChannel, setTelegramNewChannel] = useState('');
    const [telegramValidationOpen, setTelegramValidationOpen] = useState(false);
    const [telegramValidationLoading, setTelegramValidationLoading] = useState(false);
    const [telegramValidationResult, setTelegramValidationResult] = useState<ChannelValidationResult | null>(null);
    const [telegramPreview, setTelegramPreview] = useState<TelegramPreview | null>(null);
    const [telegramPreviewLoading, setTelegramPreviewLoading] = useState(false);
    const [telegramRunLoading, setTelegramRunLoading] = useState(false);
    const [telegramJobId, setTelegramJobId] = useState<string | null>(null);
    const [telegramJobStatus, setTelegramJobStatus] = useState<TelegramJobStatus | null>(null);
    const [telegramCancelLoading, setTelegramCancelLoading] = useState(false);
    const [telegramLimit, setTelegramLimit] = useState(100);
    const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
    const [telegramMessages, setTelegramMessages] = useState<TelegramMessage[]>([]);
    const [telegramMsgTotal, setTelegramMsgTotal] = useState(0);
    const [showTelegramMessages, setShowTelegramMessages] = useState(false);
    const [showTelegramIntel, setShowTelegramIntel] = useState(false);
    const [scraperList, setScraperList] = useState<ScraperListItem[]>([]);
    const [scraperStatus, setScraperStatus] = useState<Record<string, { last_run: string | null; status: string }>>({});

    // Orchestrator state
    const [isOrchestratorOpen, setIsOrchestratorOpen] = useState(false);
    const [availableScrapers, setAvailableScrapers] = useState<Record<string, any>>({});
    const [selectedScraper, setSelectedScraper] = useState('');
    const [scraperRunning, setScraperRunning] = useState(false);
    const [scraperJob, setScraperJob] = useState<{ job_id?: string; status?: string; error?: string | null; name?: string } | null>(null);

    // Pulse Feed state
    const [pulse, setPulse] = useState<PulseItem[]>([]);
    const [pulseFilter, setPulseFilter] = useState('all');
    const [upstreamProviders, setUpstreamProviders] = useState<UpstreamProviderRow[]>([]);
    const [upstreamSummary, setUpstreamSummary] = useState<UpstreamProvidersPayload['summary']>();
    const [upstreamError, setUpstreamError] = useState(false);

    const fetchUpstreamProviders = async () => {
        try {
            const data = (await systemService.getUpstreamProviders()) as UpstreamProvidersPayload;
            setUpstreamProviders(data.providers || []);
            setUpstreamSummary(data.summary);
            setUpstreamError(false);
        } catch (error) {
            console.error('Failed to load upstream providers:', error);
            setUpstreamError(true);
        }
    };

    const refreshAll = useCallback(() => {
        fetchDetailedStats();
        fetchRecentEntities();
        fetchScraperList();
        fetchScraperStatus();
        fetchPulse();
        fetchUpstreamProviders();
        setLastRefreshed(new Date());
    }, []);

    useEffect(() => {
        refreshAll();

        // Auto-refresh every 60s
        const interval = window.setInterval(refreshAll, 60000);
        return () => window.clearInterval(interval);
    }, []);

    const fetchDetailedStats = async (showLoader = true) => {
        try {
            if (showLoader) setLoading(true);
            // Cold detailed-stats can exceed the global 30s axios default (BE
            // comment: >70s uncached over the entities table). IntelligenceHub
            // already uses 45s — match that headroom so READY sources don't
            // show a false yellow banner + zeros on timeout.
            const { data } = await api.get('/scrapers/detailed-stats', { timeout: 90_000 });
            setDetailedStats(data || {});
            setStatsError(false);
        } catch (error) {
            console.error('Failed to fetch detailed stats:', error);
            setStatsError(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchTelegramChannels = async () => {
        try {
            const { data } = await api.get('/scrapers/telegram/channels');
            const channels = data.channels || [];
            setTelegramChannels(channels);
            setTelegramSelected(channels.filter((c: any) => c.enabled).map((c: any) => c.name));
        } catch (error) {
            console.error('Failed to fetch telegram channels:', error);
        }
    };

    const fetchTelegramMessages = async (jobId?: string | null) => {
        try {
            const params: any = { limit: 20 };
            const activeJobId = jobId !== undefined ? jobId : telegramJobId;
            if (activeJobId) {
                params.job_id = activeJobId;
            }
            const { data } = await api.get('/scrapers/telegram/messages', { params });
            setTelegramMessages(data.messages || []);
            setTelegramMsgTotal(data.total || 0);
        } catch (error) {
            console.error('Failed to fetch telegram messages:', error);
        }
    };

    const openTelegramModal = async () => {
        setTelegramModalOpen(true);
        await fetchTelegramChannels();
    };

    // Step 1: validate the handle on Telegram (no DB write yet) and open the
    // confirmation modal. The actual insert only fires from confirmAddTelegramChannel.
    const startAddTelegramChannel = async () => {
        const raw = telegramNewChannel.trim();
        if (!raw) return;
        setTelegramValidationOpen(true);
        setTelegramValidationLoading(true);
        setTelegramValidationResult(null);
        try {
            const { data } = await api.post<ChannelValidationResult>(
                '/scrapers/telegram/channels/validate',
                { name: raw },
            );
            setTelegramValidationResult(data);
        } catch (error: any) {
            console.error('Channel validation failed:', error);
            setTelegramValidationResult({
                input: raw,
                normalized: raw,
                valid_format: true,
                exists: null,
                reason: error?.response?.data?.detail || 'Validation request failed.',
            });
        } finally {
            setTelegramValidationLoading(false);
        }
    };

    const confirmAddTelegramChannel = async () => {
        const raw = (telegramValidationResult?.normalized || telegramNewChannel).trim();
        if (!raw) { closeTelegramValidation(); return; }
        try {
            const { data } = await api.post('/scrapers/telegram/channels', { name: raw });
            setTelegramChannels(data.channels || []);
            setTelegramSelected((data.channels || []).filter((c: any) => c.enabled).map((c: any) => c.name));
            const stored = data?.channel?.name || raw;
            toast.success(`Channel added: @${stored}`);
            setTelegramNewChannel('');
            closeTelegramValidation();
        } catch (error: any) {
            console.error('Failed to add telegram channel:', error);
            toast.error(error?.response?.data?.detail || 'Failed to add channel');
        }
    };

    const closeTelegramValidation = () => {
        setTelegramValidationOpen(false);
        setTelegramValidationLoading(false);
        setTelegramValidationResult(null);
    };

    const previewTelegramScrape = async () => {
        setTelegramPreviewLoading(true);
        try {
            const { data } = await api.post('/scrapers/telegram/preview', { channels: telegramSelected, per_channel_limit: telegramLimit });
            setTelegramPreview(data);
        } catch (error) {
            console.error('Failed to preview telegram scrape:', error);
        } finally {
            setTelegramPreviewLoading(false);
        }
    };

    const runTelegramScrape = async () => {
        setTelegramRunLoading(true);
        try {
            const { data } = await api.post('/scrapers/telegram/run', { channels: telegramSelected, per_channel_limit: telegramLimit });
            setTelegramJobId(data.job_id);
            setTelegramJobStatus({ status: data.status });
        } catch (error) {
            console.error('Failed to run telegram scrape:', error);
        } finally {
            setTelegramRunLoading(false);
        }
    };

    const cancelTelegramScrape = async () => {
        if (!telegramJobId) return;
        setTelegramCancelLoading(true);
        try {
            await api.post(`/scrapers/telegram/run/${telegramJobId}/cancel`);
            setTelegramJobStatus((prev) => ({ ...(prev || { status: 'running' }), status: 'cancelling' }));
            toast.message('Cancelling Telegram scrape…');
        } catch (error: any) {
            console.error('Failed to cancel telegram scrape:', error);
            toast.error(error?.response?.data?.detail || 'Cancel failed');
        } finally {
            setTelegramCancelLoading(false);
        }
    };

    useEffect(() => {
        let interval: number | undefined;
        const poll = async () => {
            if (!telegramJobId) return;
            try {
                const { data } = await api.get(`/scrapers/telegram/run/${telegramJobId}`);
                setTelegramJobStatus(data);
                if (data.status === 'completed' || data.status === 'error' || data.status === 'cancelled') {
                    window.clearInterval(interval);
                    fetchDetailedStats();
                    if (data.status === 'completed') {
                        fetchTelegramMessages(telegramJobId);
                        setShowTelegramMessages(true);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch telegram job status:', error);
            }
        };

        if (telegramJobId) {
            poll();
            interval = window.setInterval(poll, 2500);
        }

        return () => {
            if (interval) window.clearInterval(interval);
        };
    }, [telegramJobId]);

    const fetchRecentEntities = async () => {
        try {
            const { data } = await api.get('/entities', { params: { limit: 20, sort_by: 'newest' } });
            setRecentEntities(data || []);
        } catch (error) {
            console.error('Failed to fetch recent entities:', error);
        }
    };

    const fetchScraperList = async () => {
        try {
            const { data } = await api.get('/scrapers/list');
            if (Array.isArray(data)) {
                setScraperList(data as ScraperListItem[]);
                return;
            }

            // Backend currently returns { scrapers: { file.py: { name, description, ... } } }
            const scrapersObj = data?.scrapers;
            if (scrapersObj && typeof scrapersObj === 'object') {
                const normalized: ScraperListItem[] = Object.values(scrapersObj).map((s: any) => ({
                    name: s?.name || 'Unknown Scraper',
                    description: s?.description || '',
                    enabled: s?.enabled ?? true,
                }));
                setScraperList(normalized);
                return;
            }

            setScraperList([]);
        } catch (error) {
            console.error('Failed to fetch scraper list:', error);
        }
    };

    const fetchScraperStatus = async () => {
        try {
            const { data } = await api.get('/scrapers/status');
            if (Array.isArray(data)) {
                const normalized = (data as ScraperStatusItem[]).reduce((acc, item) => {
                    const row = {
                        last_run: item.last_run ?? null,
                        status: item.status || 'success',
                    };
                    if (item.scraper_name) acc[item.scraper_name] = row;
                    if (item.name) acc[item.name] = row;
                    return acc;
                }, {} as Record<string, { last_run: string | null; status: string }>);
                setScraperStatus(normalized);
                return;
            }

            setScraperStatus(data || {});
        } catch (error) {
            console.error('Failed to fetch scraper status:', error);
            setScraperStatus({});
        }
    };

    const fetchPulse = async () => {
        try {
            const { data } = await api.get('/scrapers/pulse?limit=50');
            setPulse(data?.pulse || []);
        } catch { /* pulse is non-critical */ }
    };

    const openOrchestrator = async () => {
        setIsOrchestratorOpen(true);
        try {
            const { data } = await api.get('/scrapers/list');
            setAvailableScrapers(data?.scrapers || {});
        } catch (error) {
            console.error('Failed to fetch scrapers for orchestrator:', error);
        }
    };

    const selectedScraperInfo = selectedScraper ? availableScrapers[selectedScraper] : null;
    const selectedScraperRunnable = selectedScraperInfo?.runnable !== false;

    const runSelectedScraper = async () => {
        if (!selectedScraper || !selectedScraperRunnable) return;
        setScraperRunning(true);
        setScraperJob(null);
        try {
            const { data } = await api.post('/scrapers/run', { scraper: selectedScraper });
            setScraperJob({
                job_id: data?.job_id,
                status: data?.status || 'started',
                name: selectedScraperInfo?.name || selectedScraper,
            });
            toast.message(`${selectedScraperInfo?.name || 'Scraper'} started. Status will update here.`);
        } catch (error: any) {
            console.error('Failed to run scraper:', error);
            const detail = error?.response?.data?.detail || 'Deploy failed';
            setScraperJob({ status: 'error', error: typeof detail === 'string' ? detail : 'Deploy failed' });
            toast.error(typeof detail === 'string' ? detail : 'Deploy failed');
        } finally {
            setScraperRunning(false);
        }
    };

    useEffect(() => {
        const jobId = scraperJob?.job_id;
        if (!jobId) return;
        if (scraperJob?.status === 'success' || scraperJob?.status === 'error') return;
        let cancelled = false;
        const poll = async () => {
            try {
                const { data } = await api.get(`/scrapers/run/${jobId}`);
                if (cancelled) return;
                setScraperJob(data);
                if (data?.status === 'success' || data?.status === 'error') {
                    // The bell notification is written just after the job settles.
                    window.setTimeout(() => { void useNotificationStore.getState().fetchUnreadCount(); }, 3000);
                }
                if (data?.status === 'success') {
                    toast.success(`${data?.name || 'Scraper'} finished. Refreshing source counts.`);
                    fetchDetailedStats(false);
                } else if (data?.status === 'error') {
                    toast.error(data?.error || 'Scraper failed');
                }
            } catch (error) {
                console.error('Failed to fetch scraper job status:', error);
            }
        };
        poll();
        const interval = window.setInterval(poll, 2500);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [scraperJob?.job_id, scraperJob?.status]);

    const formatNumber = (n: number) => n?.toLocaleString() || '0';
    const formatVolume = (n: number) => {
        if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
        return n?.toFixed(0) || '0';
    };
    const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : 'Never';
    const formatChainList = (chains?: { [key: string]: number }) => {
        if (!chains) return [];
        return Object.entries(chains)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([name]) => name);
    };

    const totalRecords =
        (detailedStats.binance_p2p?.total_ads || 0) +
        (detailedStats.telegram?.total_messages || 0) +
        (detailedStats.entities?.total || 0);

    const totalSources = (detailedStats.entities?.top_sources?.length || 0);

    return (
        <div className={`p-8`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8 flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold mb-2 flex items-center gap-3">
                            <Database className="w-7 h-7 text-primary" />
                            Data Sources
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Ingestion health for intelligence feeds plus upstream blockchain API status.
                            No API keys are shown here.
                            {lastRefreshed && (
                                <span className="text-xs ml-2 text-muted-foreground">
                                    (updated {lastRefreshed.toLocaleTimeString()})
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={openOrchestrator}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 flex items-center gap-2"
                        >
                            <Play className="w-4 h-4" />
                            Run ingest job
                        </button>
                        <button
                            onClick={refreshAll}
                            disabled={loading}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {statsError && (
                    <div className="mb-6 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0" />
                            Could not load source stats — numbers below may be incomplete or zero.
                        </span>
                        <button type="button" onClick={refreshAll} className="text-primary text-xs hover:underline shrink-0">
                            Retry
                        </button>
                    </div>
                )}

                {/* Upstream API providers (circuit breaker + configured flag) */}
                <div className="mb-8 rounded-lg border border-border bg-card">
                    <div className="px-6 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                <Shield className="w-5 h-5 text-primary" />
                                Upstream API providers
                            </h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Live configuration and circuit-breaker state — no secrets, no probe traffic.
                                Super-admins can run live probes from Settings → Providers.
                            </p>
                        </div>
                        {upstreamSummary && (
                            <div className="flex flex-wrap gap-2 text-xs">
                                <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground">
                                    {upstreamSummary.ready ?? 0} ready
                                </span>
                                {(upstreamSummary.degraded ?? 0) > 0 && (
                                    <span className="px-2 py-1 rounded-md bg-muted text-muted-foreground">
                                        {upstreamSummary.degraded} degraded
                                    </span>
                                )}
                                {(upstreamSummary.circuit_open ?? 0) > 0 && (
                                    <span className="px-2 py-1 rounded-md bg-destructive/10 text-destructive">
                                        {upstreamSummary.circuit_open} paused
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {upstreamError ? (
                        <div className="px-6 py-4 text-sm text-muted-foreground flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            Could not load upstream provider status.
                        </div>
                    ) : upstreamProviders.length === 0 ? (
                        <div className="px-6 py-4 text-sm text-muted-foreground">Loading provider registry…</div>
                    ) : (
                        <div className="divide-y divide-border max-h-80 overflow-y-auto">
                            {upstreamProviders.map((p) => (
                                <div key={p.name} className="px-6 py-3 flex items-start justify-between gap-4 hover:bg-muted/30">
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm">{p.display_name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5 capitalize">
                                            {p.category.replace(/_/g, ' ')}
                                            {p.chains.length > 0 && (
                                                <span> · {p.chains.join(', ')}{p.chains.length >= 6 ? '…' : ''}</span>
                                            )}
                                        </div>
                                        {p.status === 'circuit_open' && p.trip_reason && (
                                            <div className="text-xs text-destructive mt-1 truncate" title={p.trip_reason}>
                                                {p.trip_reason}
                                            </div>
                                        )}
                                    </div>
                                    <UpstreamProviderBadge status={p.status} tripReason={p.trip_reason} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-muted-foreground text-sm">Total Records</span>
                            <TrendingUp className="w-5 h-5 text-primary" />
                        </div>
                        <div className="text-3xl font-bold">{formatNumber(totalRecords)}</div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-muted-foreground text-sm">Data Sources</span>
                            <Database className="w-5 h-5 text-cyan-500" />
                        </div>
                        <div className="text-3xl font-bold">{totalSources}+</div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-muted-foreground text-sm">Unique UPI IDs</span>
                            <CreditCard className="w-5 h-5 text-green-500" />
                        </div>
                        <div className="text-3xl font-bold">{formatNumber(detailedStats.telegram?.unique_upi_ids || 0)}</div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-muted-foreground text-sm">Crypto Wallets</span>
                            <Wallet className="w-5 h-5 text-primary" />
                        </div>
                        <div className="text-3xl font-bold">{formatNumber(detailedStats.telegram?.unique_wallets || 0)}</div>
                    </div>

                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-muted-foreground text-sm">High Risk Entities</span>
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div className="text-3xl font-bold text-red-500">
                            {formatNumber((detailedStats.entities?.critical_risk || 0) + (detailedStats.entities?.high_risk || 0))}
                        </div>
                    </div>
                </div>

                {/* Data Sources Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

                    {/* Binance P2P Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-yellow-500/10 rounded-lg">
                                <Globe className="w-6 h-6 text-yellow-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Binance P2P</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.binance_p2p?.last_run || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.binance_p2p?.last_run} rowCount={detailedStats.binance_p2p?.total_ads} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Total Ads</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.binance_p2p?.total_ads || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Traders</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.binance_p2p?.unique_traders || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Sell Volume</div>
                                <div className="text-xl font-bold text-green-500">{formatVolume(detailedStats.binance_p2p?.sell_volume || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Buy Volume</div>
                                <div className="text-xl font-bold text-primary">{formatVolume(detailedStats.binance_p2p?.buy_volume || 0)}</div>
                            </div>
                        </div>

                        {detailedStats.binance_p2p?.assets && detailedStats.binance_p2p.assets.length > 0 && (
                            <div className="border-t border-border pt-3">
                                <div className="text-xs text-muted-foreground mb-2">Assets</div>
                                <div className="flex flex-wrap gap-1">
                                    {detailedStats.binance_p2p.assets.slice(0, 5).map(a => (
                                        <span key={a.name} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                                            {a.name}: {a.count}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Telegram Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-primary/10 rounded-lg">
                                <MessageCircle className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Telegram Intel</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.telegram?.last_run || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.telegram?.last_run} rowCount={detailedStats.telegram?.total_messages} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Messages</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.telegram?.total_messages || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Channels</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.telegram?.channels_scraped || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">UPI IDs</div>
                                <div className="text-xl font-bold text-green-500">{formatNumber(detailedStats.telegram?.unique_upi_ids || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Wallets</div>
                                <div className="text-xl font-bold text-primary">{formatNumber(detailedStats.telegram?.unique_wallets || 0)}</div>
                            </div>
                        </div>

                        {detailedStats.telegram?.top_channels && detailedStats.telegram.top_channels.length > 0 && (
                            <div className="border-t border-border pt-3">
                                <div className="text-xs text-muted-foreground mb-2">Top Channels</div>
                                <div className="flex flex-wrap gap-1">
                                    {detailedStats.telegram.top_channels.slice(0, 4).map(c => (
                                        <span key={c.name} className="px-2 py-0.5 bg-primary/10 text-foreground border border-primary/20 rounded text-xs truncate max-w-[120px]">
                                            {c.name}: {c.count}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-4">
                            <button
                                onClick={() => navigate('/telegram')}
                                className="w-full px-4 py-2.5 text-sm font-medium bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Open Telegram Intelligence
                            </button>
                        </div>
                    </div>

                    {/* Entities Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-red-500/10 rounded-lg">
                                <Database className="w-6 h-6 text-red-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Entity Database</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.entities?.last_run || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.entities?.last_run} rowCount={detailedStats.entities?.total} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Total</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.entities?.total || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">India-Linked</div>
                                <div className="text-xl font-bold text-orange-500">{formatNumber(detailedStats.entities?.india_linked || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Critical Risk</div>
                                <div className="text-xl font-bold text-red-500">{formatNumber(detailedStats.entities?.critical_risk || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">High Risk</div>
                                <div className="text-xl font-bold text-yellow-500">{formatNumber(detailedStats.entities?.high_risk || 0)}</div>
                            </div>
                        </div>

                        {detailedStats.entities?.top_sources && detailedStats.entities.top_sources.length > 0 && (
                            <div className="border-t border-border pt-3">
                                <div className="text-xs text-muted-foreground mb-2">Sources Breakdown</div>
                                <div className="space-y-2">
                                    {detailedStats.entities.top_sources.slice(0, 5).map(s => {
                                        const percent = ((s.count / (detailedStats.entities?.total || 1)) * 100).toFixed(1);
                                        return (
                                            <div key={s.name} className="flex flex-col gap-1">
                                                <div className="flex justify-between text-xs">
                                                    <span className="capitalize">{s.name.replace(/_/g, ' ')}</span>
                                                    <span className="text-muted-foreground">{formatNumber(s.count)} ({percent}%)</span>
                                                </div>
                                                <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-red-500/70 rounded-full"
                                                        style={{ width: `${Math.min(parseFloat(percent), 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Wallet Enrichment Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-green-500/10 rounded-lg">
                                <Coins className="w-6 h-6 text-green-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Wallet Enrichment</h3>
                                <p className="text-xs text-muted-foreground">On-chain balance lookups</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.wallet_enrichment?.last_run} rowCount={detailedStats.wallet_enrichment?.total_enriched} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Enriched</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.wallet_enrichment?.total_enriched || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">High Value</div>
                                <div className="text-xl font-bold text-green-500">{formatNumber(detailedStats.wallet_enrichment?.high_value_wallets || 0)}</div>
                            </div>
                        </div>

                        {detailedStats.wallet_enrichment?.top_wallets && detailedStats.wallet_enrichment.top_wallets.length > 0 && (
                            <div className="border-t border-border pt-3">
                                <div className="text-xs text-muted-foreground mb-2">Top Wallets by Balance</div>
                                <div className="space-y-1">
                                    {detailedStats.wallet_enrichment.top_wallets.slice(0, 3).map(w => (
                                        <div key={w.address} className="flex justify-between text-xs">
                                            <span className="font-mono text-muted-foreground">{w.address}</span>
                                            <span className="text-green-500 font-medium">{w.balance.toFixed(2)} TRX</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Bitcointalk Scam Intel Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-orange-500/10 rounded-lg">
                                <MessageSquare className="w-6 h-6 text-orange-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Bitcointalk Scam Intel</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.bitcointalk?.last_scraped || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.bitcointalk?.last_scraped} rowCount={detailedStats.bitcointalk?.total_reports} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Scam Reports</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.bitcointalk?.total_reports || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Flagged Addresses</div>
                                <div className="text-xl font-bold text-orange-500">{formatNumber(detailedStats.bitcointalk?.unique_addresses || 0)}</div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-3">
                            <div className="text-xs text-muted-foreground mb-2">Chains Covered</div>
                            <div className="flex flex-wrap gap-1">
                                {formatChainList(detailedStats.bitcointalk?.chains).length > 0 ? (
                                    formatChainList(detailedStats.bitcointalk?.chains).map((chain) => (
                                        <span key={chain} className="px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs">
                                            {chain}
                                        </span>
                                    ))
                                ) : (
                                    <span className="px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs">BTC</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Chainabuse Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-rose-500/10 rounded-lg">
                                <AlertTriangle className="w-6 h-6 text-rose-500" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Chainabuse</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.chainabuse?.last_run || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.chainabuse?.last_run} rowCount={detailedStats.chainabuse?.total} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Entities</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.chainabuse?.total || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Chains</div>
                                <div className="text-xl font-bold text-rose-500">{formatNumber(Object.keys(detailedStats.chainabuse?.chains || {}).length)}</div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-3">
                            <div className="text-xs text-muted-foreground mb-2">Top Chains</div>
                            <div className="flex flex-wrap gap-1">
                                {formatChainList(detailedStats.chainabuse?.chains).length > 0 ? (
                                    formatChainList(detailedStats.chainabuse?.chains).map((chain) => (
                                        <span key={chain} className="px-2 py-0.5 bg-rose-500/10 text-rose-500 rounded text-xs">
                                            {chain}
                                        </span>
                                    ))
                                ) : (
                                    <span className="px-2 py-0.5 bg-rose-500/10 text-rose-500 rounded text-xs">N/A</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Reddit Deep Scam Intel Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-muted rounded-lg">
                                <BookOpen className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Reddit Scam Intel</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.reddit?.last_scraped || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.reddit?.last_scraped} rowCount={detailedStats.reddit?.total_reports} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Scam Reports</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.reddit?.total_reports || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Flagged Addresses</div>
                                <div className="text-xl font-bold text-foreground">{formatNumber(detailedStats.reddit?.unique_addresses || 0)}</div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-3">
                            <div className="text-xs text-muted-foreground">
                                Sourced from r/CryptoCurrency, r/Bitcoin, r/Scams
                            </div>
                        </div>
                    </div>

                    {/* eCourts / Indian Kanoon Card */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-primary/10 rounded-lg">
                                <Scale className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">eCourts / Indian Kanoon</h3>
                                <p className="text-xs text-muted-foreground">Last: {formatDate(detailedStats.ecourts?.last_scraped || null)}</p>
                            </div>
                            <SourceHealthBadge lastRun={detailedStats.ecourts?.last_scraped} rowCount={detailedStats.ecourts?.total_articles} />
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Court Articles</div>
                                <div className="text-xl font-bold">{formatNumber(detailedStats.ecourts?.total_articles || 0)}</div>
                            </div>
                            <div className="bg-secondary/30 rounded p-3">
                                <div className="text-xs text-muted-foreground">Crypto Addresses</div>
                                <div className="text-xl font-bold text-primary">{formatNumber(detailedStats.ecourts?.unique_addresses || 0)}</div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-3">
                            <div className="text-xs text-muted-foreground">
                                Validated addresses from Indian court orders
                            </div>
                        </div>
                    </div>

                    {/* Additional Scrapers from API (skip ones already shown as hardcoded cards) */}
                    {scraperList
                        .filter(scraper => {
                            const n = scraper.name.toLowerCase();
                            return !HARDCODED_SCRAPER_NAME_FRAGMENTS.some(frag => n.includes(frag));
                        })
                        .map((scraper) => {
                        const status = scraperStatus[scraper.name] || { last_run: null, status: 'unknown' };
                        const displayName = scraper.name.replace(/\.py$/i, '').replace(/[_-]+/g, ' ');

                        return (
                            <div key={scraper.name} className="bg-card border border-border rounded-lg p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-3 bg-muted rounded-lg">
                                        <Database className="w-6 h-6 text-muted-foreground" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-lg capitalize">{displayName}</h3>
                                        <p className="text-xs text-muted-foreground">
                                            {status.last_run ? `Last: ${formatDate(status.last_run)}` : 'Never run'}
                                        </p>
                                    </div>
                                    <SourceHealthBadge lastRun={status.last_run} status={status.status} />
                                </div>

                                <div className="bg-secondary/30 rounded p-3 mb-4">
                                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground border border-border">
                                            {status.status === 'error' ? 'Error' : status.status === 'success' ? 'OK' : (status.status || 'Unknown')}
                                        </span>
                                        {scraper.enabled ? (
                                            <span className="text-xs text-muted-foreground">Enabled</span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">Disabled</span>
                                        )}
                                    </div>
                                </div>

                                <div className="border-t border-border pt-3">
                                    <div className="text-xs text-muted-foreground">
                                        {scraper.description || 'No description available'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                </div>

                {/* Telegram Intelligence Panel — right after cards grid so it's visible on click */}
                <TelegramIntelPanel isOpen={showTelegramIntel} onClose={() => setShowTelegramIntel(false)} />

                {/* Scraped Telegram Messages Panel */}
                {showTelegramMessages && (
                    <div className="bg-card border border-primary/20 rounded-lg p-6 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <MessageCircle className="w-5 h-5 text-primary" />
                                Scraped Telegram Messages
                                <span className="text-xs font-normal text-muted-foreground ml-2">({telegramMsgTotal.toLocaleString()} total)</span>
                            </h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => navigate('/p2p?source=telegram')}
                                    className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground text-foreground rounded hover:bg-brand-gold-hover flex items-center gap-1"
                                >
                                    <ExternalLink className="w-3 h-3" />
                                    View in P2P Intelligence
                                </button>
                                <button
                                    onClick={() => setShowTelegramMessages(false)}
                                    className="text-muted-foreground hover:text-foreground text-sm px-2"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {telegramMessages.length > 0 ? (
                            <div className="space-y-3 max-h-[500px] overflow-auto">
                                {telegramMessages.map((msg) => (
                                    <div key={msg.id} className="p-3 border border-border rounded-lg hover:border-primary/30 transition-colors">
                                        <div className="flex justify-between text-[11px] text-muted-foreground mb-2">
                                            <span className="font-medium text-primary">#{msg.channel}</span>
                                            <div className="flex items-center gap-3">
                                                <span>by {msg.sender}</span>
                                                <span>{msg.scraped_at ? new Date(msg.scraped_at).toLocaleString() : ''}</span>
                                            </div>
                                        </div>
                                        <div className="text-sm text-foreground mb-2 whitespace-pre-wrap break-words overflow-y-auto max-h-40">
                                            {msg.content}
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-[10px]">
                                            {msg.extracted_exchanges && msg.extracted_exchanges.length > 0 && msg.extracted_exchanges.map((ex, i) => (
                                                <span key={`ex-${i}`} className="px-2 py-0.5 bg-primary/10 text-primary rounded border border-primary/20 font-bold uppercase">
                                                    EXCHANGE: {ex}
                                                </span>
                                            ))}
                                            {msg.upi_ids.length > 0 && msg.upi_ids.map((upi, i) => (
                                                <span key={`upi-${i}`} className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded border border-green-500/20 font-mono">
                                                    UPI: {upi}
                                                </span>
                                            ))}
                                            {msg.crypto_addresses.length > 0 && msg.crypto_addresses.map((addr, i) => (
                                                <span key={`addr-${i}`} className="px-2 py-0.5 bg-primary/10 text-foreground rounded border border-primary/20 font-mono">
                                                    {addr.startsWith('T') ? 'TRX' : addr.startsWith('0x') ? 'ETH' : 'ADDR'}: {addr.substring(0, 8)}...{addr.substring(addr.length - 6)}
                                                </span>
                                            ))}
                                            {msg.upi_ids.length === 0 && msg.crypto_addresses.length === 0 && (!msg.extracted_exchanges || msg.extracted_exchanges.length === 0) && (
                                                <span className="text-muted-foreground">No identifiers extracted</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No scraped messages found. Run a Telegram scrape to collect data.
                            </div>
                        )}
                    </div>
                )}

                {/* Pulse Feed (absorbed from Intelligence Hub) */}
                {pulse.length > 0 && (
                    <div className="bg-card border border-border rounded-lg p-6 mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold flex items-center gap-2">
                                <Activity className="w-5 h-5 text-green-500" />
                                The Pulse
                                <span className="ml-2 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-xs text-green-400 font-normal">LIVE</span>
                            </h2>
                            <select
                                className="bg-background border border-border rounded px-3 py-1.5 text-xs"
                                value={pulseFilter}
                                onChange={(e) => setPulseFilter(e.target.value)}
                            >
                                <option value="all">All Events</option>
                                <option value="telegram">Telegram</option>
                                <option value="p2p_anomaly">P2P Anomalies</option>
                                <option value="entity">Flagged Threats</option>
                            </select>
                        </div>

                        <div className="relative space-y-3 max-h-[400px] overflow-auto">
                            <div className="absolute left-[18px] top-0 bottom-0 w-px bg-border" />
                            {pulse
                                .filter(p => pulseFilter === 'all' || p.type === pulseFilter)
                                .slice(0, 20)
                                .map((item, idx) => {
                                    const badgeClass = PULSE_TYPE_CLASS[item.type] || 'bg-muted text-muted-foreground';
                                    return (
                                        <div key={idx} className="flex items-start gap-3 relative pl-10">
                                            <div className="absolute left-2.5 top-2 w-3 h-3 rounded-full border-2 border-border bg-background z-10" />
                                            <div className="flex-1 bg-background border border-border rounded-lg p-3">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${badgeClass}`}>
                                                        {item.source}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        {new Date(item.timestamp).toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-foreground/80 max-h-16 overflow-hidden">
                                                    {item.content}
                                                </p>
                                                {(item.identifiers.length > 0 || item.crypto.length > 0) && (
                                                    <div className="flex flex-wrap gap-1 mt-2">
                                                        {item.identifiers.slice(0, 3).map((id, i) => (
                                                            <span key={`id-${i}`} className="text-[10px] px-1.5 py-0.5 bg-muted text-foreground rounded font-mono">{id}</span>
                                                        ))}
                                                        {item.crypto.slice(0, 3).map((c, i) => (
                                                            <button
                                                                key={`c-${i}`}
                                                                type="button"
                                                                onClick={() => navigate(`/workbench?address=${encodeURIComponent(c)}`)}
                                                                className="text-[10px] px-1.5 py-0.5 bg-muted text-foreground rounded font-mono hover:text-primary hover:underline"
                                                            >
                                                                {c.substring(0, 12)}…
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            {pulse.filter(p => pulseFilter === 'all' || p.type === pulseFilter).length === 0 && (
                                <div className="text-center py-4 text-muted-foreground text-sm">No events match this filter.</div>
                            )}
                        </div>
                    </div>
                )}


                {/* Recent Additions Table */}
                <div className="bg-card border border-border rounded-lg p-6">
                    <h2 className="text-xl font-semibold mb-4">Recent Additions</h2>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Address</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Chain</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Source</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Entity</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Risk</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Added</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentEntities.length > 0 ? (
                                    recentEntities.slice(0, 10).map((entity, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b border-border/50 hover:bg-secondary/50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/workbench?chain=${encodeURIComponent(entity.chain)}&address=${encodeURIComponent(entity.address)}`)}
                                        >
                                            <td className="py-3 px-4 font-mono text-xs text-primary hover:underline">
                                                {entity.address.substring(0, 12)}...{entity.address.substring(entity.address.length - 8)}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                                                    {entity.chain}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-sm">{entity.source}</td>
                                            <td className="py-3 px-4 text-sm">{entity.entity_name || '-'}</td>
                                            <td className="py-3 px-4">
                                                {entity.risk_score !== undefined && (
                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-muted text-muted-foreground border border-border">
                                                        {(entity.risk_score * 100).toFixed(0)}%
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-xs text-muted-foreground">
                                                {new Date(entity.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-muted-foreground">
                                            {loading ? 'Loading...' : 'No recent additions'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Telegram Scraper Modal */}
                {telegramModalOpen && (
                    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                        <div className="w-full max-w-3xl bg-card border border-border rounded-lg p-6 max-h-[90vh] overflow-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">Telegram Scraper</h3>
                                <button
                                    onClick={() => setTelegramModalOpen(false)}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Channels</h4>
                                    <div className="flex gap-2 mb-2">
                                        <button
                                            onClick={() => setTelegramSelected(telegramChannels.map(c => c.name))}
                                            className="px-2 py-1 text-[10px] font-medium border border-border rounded hover:bg-muted"
                                        >
                                            Select All
                                        </button>
                                        <button
                                            onClick={() => setTelegramSelected([])}
                                            className="px-2 py-1 text-[10px] font-medium border border-border rounded hover:bg-muted"
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    <div className="space-y-2 max-h-48 overflow-auto border border-border rounded p-2">
                                        {telegramChannels.length > 0 ? telegramChannels.map((c) => (
                                            <label key={c.name} className="flex items-center gap-2 text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={telegramSelected.includes(c.name)}
                                                    onChange={(e) => {
                                                        setTelegramSelected(prev => e.target.checked
                                                            ? [...prev, c.name]
                                                            : prev.filter(n => n !== c.name)
                                                        );
                                                    }}
                                                />
                                                <span className="font-mono truncate">{c.name}</span>
                                                {c.source === 'db' && <span className="text-[10px] text-muted-foreground">custom</span>}
                                                {c.source === 'default' && <span className="text-[10px] text-muted-foreground">default</span>}
                                                {c.source === 'discovered' && <span className="text-[10px] text-primary">discovered</span>}
                                            </label>
                                        )) : (
                                            <div className="text-xs text-muted-foreground">No channels loaded.</div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 mt-3">
                                        <input
                                            value={telegramNewChannel}
                                            onChange={(e) => setTelegramNewChannel(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') startAddTelegramChannel(); }}
                                            placeholder="Add channel (handle or t.me URL)"
                                            className="flex-1 px-2 py-1 text-xs bg-background border border-input rounded"
                                        />
                                        <button
                                            onClick={startAddTelegramChannel}
                                            disabled={!telegramNewChannel.trim() || telegramValidationLoading}
                                            className="px-3 py-1 text-xs border border-border rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold mb-2">Scrape Settings</h4>
                                    <label className="text-xs text-muted-foreground block mb-1">Per-channel limit</label>
                                    <input
                                        type="number"
                                        min={10}
                                        max={500}
                                        value={telegramLimit}
                                        onChange={(e) => setTelegramLimit(parseInt(e.target.value || '100', 10))}
                                        className="w-full px-2 py-1 text-xs bg-background border border-input rounded"
                                    />

                                    <div className="flex gap-2 mt-4">
                                        <button
                                            onClick={previewTelegramScrape}
                                            disabled={telegramPreviewLoading || telegramSelected.length === 0}
                                            className="flex-1 px-3 py-2 text-xs font-medium border border-border rounded hover:bg-muted disabled:opacity-50"
                                        >
                                            {telegramPreviewLoading ? 'Previewing...' : 'Preview'}
                                        </button>
                                        <button
                                            onClick={runTelegramScrape}
                                            disabled={telegramRunLoading || telegramSelected.length === 0}
                                            className="flex-1 px-3 py-2 text-xs font-medium bg-primary text-primary-foreground rounded hover:bg-brand-gold-hover disabled:opacity-50"
                                        >
                                            {telegramRunLoading ? 'Starting...' : 'Confirm & Save'}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-muted-foreground mt-2">
                                        Preview first, then confirm to save results to the database.
                                    </p>
                                </div>
                            </div>

                            {telegramPreview && (
                                <div className="border-t border-border pt-4">
                                    <h4 className="text-sm font-semibold mb-2">Preview Summary</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div className="bg-secondary/30 rounded p-2">
                                            <div className="text-muted-foreground">Scanned</div>
                                            <div className="text-lg font-semibold">{telegramPreview.total_scanned}</div>
                                        </div>
                                        <div className="bg-secondary/30 rounded p-2">
                                            <div className="text-muted-foreground">Relevant</div>
                                            <div className="text-lg font-semibold">{telegramPreview.relevant_messages}</div>
                                        </div>
                                        <div className="bg-secondary/30 rounded p-2">
                                            <div className="text-muted-foreground">UPIs</div>
                                            <div className="text-lg font-semibold">{telegramPreview.unique_upi_ids}</div>
                                        </div>
                                        <div className="bg-secondary/30 rounded p-2">
                                            <div className="text-muted-foreground">Wallets</div>
                                            <div className="text-lg font-semibold">{telegramPreview.unique_wallets}</div>
                                        </div>
                                    </div>

                                    {telegramPreview.samples && telegramPreview.samples.length > 0 && (
                                        <div className="mt-4">
                                            <div className="text-xs font-medium mb-2">Sample Messages</div>
                                            <div className="space-y-2 max-h-48 overflow-auto">
                                                {telegramPreview.samples.map((s: any, idx: number) => (
                                                    <div key={idx} className="p-2 border border-border rounded text-xs">
                                                        <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                                                            <span>{s.channel}</span>
                                                            <span>{s.timestamp ? new Date(s.timestamp).toLocaleString() : ''}</span>
                                                        </div>
                                                        <div className="text-foreground mb-1">{s.snippet}</div>
                                                        <div className="text-[10px] text-muted-foreground">
                                                            UPI: {s.upi_ids?.length || 0} • Wallets: {s.crypto_addresses?.length || 0}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {telegramJobId && (
                                <div className="border-t border-border pt-4 mt-4">
                                    <h4 className="text-sm font-semibold mb-2">Run Status</h4>
                                    <div className="text-xs text-muted-foreground">Job: {telegramJobId}</div>
                                    <div className="mt-2 text-xs">
                                        Status: <span className="font-medium">{telegramJobStatus?.status || 'queued'}</span>
                                    </div>
                                    {(telegramJobStatus?.status === 'queued' || telegramJobStatus?.status === 'running' || telegramJobStatus?.status === 'cancelling') && (
                                        <button
                                            type="button"
                                            onClick={cancelTelegramScrape}
                                            disabled={telegramCancelLoading}
                                            className="mt-3 px-3 py-1.5 text-xs font-medium border border-border rounded hover:bg-muted disabled:opacity-50"
                                        >
                                            {telegramCancelLoading ? 'Cancelling…' : 'Cancel scrape'}
                                        </button>
                                    )}
                                    {telegramJobStatus?.summary && (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                                            <div className="bg-secondary/30 rounded p-2">
                                                <div className="text-muted-foreground">Saved</div>
                                                <div className="text-lg font-semibold">{telegramJobStatus.summary.saved_messages}</div>
                                            </div>
                                            <div className="bg-secondary/30 rounded p-2">
                                                <div className="text-muted-foreground">Scanned</div>
                                                <div className="text-lg font-semibold">{telegramJobStatus.summary.total_scanned}</div>
                                            </div>
                                            <div className="bg-secondary/30 rounded p-2">
                                                <div className="text-muted-foreground">UPIs</div>
                                                <div className="text-lg font-semibold">{telegramJobStatus.summary.unique_upi_ids}</div>
                                            </div>
                                            <div className="bg-secondary/30 rounded p-2">
                                                <div className="text-muted-foreground">Wallets</div>
                                                <div className="text-lg font-semibold">{telegramJobStatus.summary.unique_wallets}</div>
                                            </div>
                                        </div>
                                    )}
                                    {telegramJobStatus?.error && (
                                        <div className="mt-2 text-xs text-red-500">{telegramJobStatus.error}</div>
                                    )}
                                    {telegramJobStatus?.status === 'completed' && (
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                onClick={() => { setTelegramModalOpen(false); fetchTelegramMessages(); setShowTelegramMessages(true); }}
                                                className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground text-foreground rounded hover:bg-brand-gold-hover flex items-center gap-1"
                                            >
                                                <MessageCircle className="w-3 h-3" />
                                                View Scraped Messages
                                            </button>
                                            <button
                                                onClick={() => { setTelegramModalOpen(false); navigate('/p2p?source=telegram'); }}
                                                className="px-3 py-1.5 text-xs font-medium border border-primary/30 text-primary rounded hover:bg-primary/10 flex items-center gap-1"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                P2P Intelligence
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Universal Orchestrator Modal (absorbed from Intelligence Hub) */}
            {isOrchestratorOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-card border border-border rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <Play className="w-5 h-5 text-green-500" />
                                Universal ingest runner
                            </h3>
                            <button onClick={() => setIsOrchestratorOpen(false)} className="text-muted-foreground hover:text-foreground">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-sm text-muted-foreground mb-4">
                            Select and deploy any intelligence scraper on-demand.
                        </p>
                        <div className="mb-3">
                            <DownwardSelect
                                id="scraper-deploy-picker"
                                value={selectedScraper}
                                onChange={setSelectedScraper}
                                placeholder="Select a scraper..."
                                options={Object.entries(availableScrapers).map(([key, info]) => ({
                                    value: key,
                                    label: `${(info as any)?.name || key}${(info as any)?.runnable === false ? ' (batch import)' : ''}`,
                                }))}
                            />
                        </div>
                        {selectedScraper && availableScrapers[selectedScraper] && (
                            <div className="bg-secondary/30 rounded p-3 mb-4 text-xs text-muted-foreground">
                                {(availableScrapers[selectedScraper] as any)?.description || 'No description available.'}
                            </div>
                        )}
                        {scraperJob && (
                            <div className="mb-4 rounded border border-border bg-muted/40 px-3 py-2 text-xs" data-testid="scraper-job-status">
                                <div className="font-medium text-foreground">
                                    {scraperJob.name || 'Job'} — {scraperJob.status || 'started'}
                                </div>
                                {scraperJob.job_id && (
                                    <div className="mt-1 text-muted-foreground">Job {scraperJob.job_id}</div>
                                )}
                                {scraperJob.error && (
                                    <div className="mt-1 text-destructive">{scraperJob.error}</div>
                                )}
                                {scraperJob.status === 'success' && (
                                    <div className="mt-1 text-muted-foreground">Source cards refresh when this finishes.</div>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setIsOrchestratorOpen(false)} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                                Close
                            </button>
                            <button
                                onClick={runSelectedScraper}
                                disabled={!selectedScraper || !selectedScraperRunnable || scraperRunning}
                                className="px-4 py-2 bg-green-600 text-foreground rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <Play className="w-4 h-4" />
                                {scraperRunning ? 'Deploying...' : 'Deploy Job'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ChannelValidationModal
                open={telegramValidationOpen}
                loading={telegramValidationLoading}
                result={telegramValidationResult}
                onConfirm={confirmAddTelegramChannel}
                onRetry={closeTelegramValidation}
                onCancel={closeTelegramValidation}
            />
        </div>
    );
};
